import HTMLView from '@components/HTMLView'
import ScribbleGallery from '../ScribbleGallery'
import { cssClassIn, fbCssClasses, instagramUrl, twitterUrl } from './utils'
import { FunctionComponent } from 'react'
import InstagramComponent from '@components/SocialEmbed/Instagram'
import FacebookComponent from '@components/SocialEmbed/Facebook'
import TwitterComponent from '@components/SocialEmbed/Twitter'
import DarkCoveWidget from '@widgets/Video/DarkCoveWidget'
import VideoYoutube from '@components/Video/Youtube'
import { getVideoIdFromYoutubeUrl, isYoutubeUrl } from '@widgets/Video/utils'
import { CustomProcessNode } from '@components/HTMLView/types'
import ScribbleImage from './ScribbleImage'

interface HtmlViewWrapperProps {
  content: string
  meta: any
  renderVideo: any
  renderYoutube: any
  InstagramComponent: any
  TwitterComponent: any
  FacebookComponent: any
}

const allowedHtmlTags = [
  'div',
  'span',
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'strong',
  'em',
  'u',
  's',
  'blockquote',
  'big',
  'small',
  'tt',
  'var',
  'del',
  'cite',
  'q',
  'strike',
  'sub',
  'sup',
  'ul',
  'ol',
  'li',
  'a',
  'figure',
  'figcaption',
  'img',
  'br',
  'iframe',
  'video',
]

const processNodeDiv: CustomProcessNode = (
  processDefaultNode,
  node,
  children,
  index,
  ...props
) => {
  if (node.attribs) {
    const nodeCssClass = node.attribs.class

    // Note: Instagram has the same class as galleries ("scrbbl-post-embed")
    if (cssClassIn(['scrbbl-post-embed'], nodeCssClass)) {
      if (node.attribs['data-url'] && instagramUrl(node.attribs['data-url'])) {
        return <InstagramComponent key={index} url={node.attribs['data-url']} />
      }

      return <ScribbleGallery url={node.attribs['data-url']} />
    }

    if (cssClassIn(fbCssClasses, nodeCssClass)) {
      return <FacebookComponent key={index} url={node.attribs['data-href']} />
    }
  }

  return processDefaultNode(node, children, index, ...props)
}

const processNodeBlockquote: CustomProcessNode = (
  processDefaultNode,
  node,
  children,
  index,
  ...props
) => {
  if (node.attribs) {
    const nodeCssClass = node.attribs.class

    if (cssClassIn(['twitter-tweet'], nodeCssClass)) {
      const url = twitterUrl(node)
      return url ? <TwitterComponent key={index} url={url} /> : null
    }
  }
  return processDefaultNode(node, children, index, ...props)
}

const processNodeImg: CustomProcessNode = (
  processDefaultNode,
  node,
  children,
  index,
  meta,
  ...props
) => {
  return node.attribs ? (
    <ScribbleImage
      imageUrl={node.attribs.src}
      alt={node.attribs.alt}
      meta={meta}
    />
  ) : (
    processDefaultNode(node, children, index, ...props)
  )
}

const processNodeIframe: CustomProcessNode = (_, node) => {
  const videoSrc = node?.attribs?.src
  if (isYoutubeUrl(videoSrc)) {
    const videoId = getVideoIdFromYoutubeUrl(videoSrc)
    return <VideoYoutube key={videoId} videoId={videoId} />
  }
}

const processNodeVideo: CustomProcessNode = (
  processDefaultNode,
  node,
  children,
  index,
  ...props
) => {
  if (node.attribs) {
    const nodeCssClass = node.attribs.class
    const videoId = node.attribs['data-id']

    if (cssClassIn(['video-js'], nodeCssClass) && videoId) {
      return (
        <DarkCoveWidget.component
          key={`video-scribble-${videoId}`}
          videoId={videoId}
        />
      )
    }
  }
  return processDefaultNode(node, children, index, ...props)
}

const HTMLViewWrapper: FunctionComponent<HtmlViewWrapperProps> = ({
  content,
  meta,
}) => {
  const processNodeRuleForTag = {
    div: {
      processNode: processNodeDiv,
    },
    blockquote: {
      processNode: processNodeBlockquote,
    },
    img: {
      processNode: processNodeImg,
      meta: meta,
    },
    iframe: {
      processNode: processNodeIframe,
    },
    video: {
      processNode: processNodeVideo,
    },
  }

  return (
    <HTMLView
      content={content}
      allowedHtmlTags={allowedHtmlTags}
      processNodeRuleForTag={processNodeRuleForTag}
    />
  )
}

export default HTMLViewWrapper
